import { FC } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import styled from 'styled-components';
import { theme } from '../../theme';
import moment from 'moment';
import { MeasurementsList } from '../../services/Measurement';
import { numberToMinutes } from '../../utils/numberToMinutes';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const Container = styled.div(({ theme }) => {
  const { color, shadow } = theme;

  return `
      background-color: ${color.grayscale[1000]};
      border-radius: 16px;
      box-shadow: ${shadow.dialog};
      padding: 38px 32px;
      width: max(89%, 1284px);
      margin: 0 auto;

      @media print {
        width: 100vw;
        padding: 32px 0;
        margin: 0;
        transform: scale(0.8);
      }
    `;
});

export interface IMeasurementChart {
  data: MeasurementsList;
}

const MeasurementChart: FC<IMeasurementChart> = ({ data }) => {
  const { primary, secondary, accentPrimary, grayscale } = theme.color;
  const { type } = theme;
  const labels = data.measurement.map(({ timestamp }) => moment(timestamp).format('M/D H:mm A'));
  const rawData = data.measurement.map(({ value }) => value);
  const cogData = data.cognitive_assistance.map(({ value }) => value);
  const physData = data.physical_assistance.map(({ value }) => value);
  const resistanceData = data.resistance.map(({ value }) => value);
  const isStandingDuration = data.header.title.toLowerCase().trim().endsWith('endurance');
  const _data = {
    labels,
    datasets: [
      {
        label: isStandingDuration ? 'Standing Duration' : data.header.title,
        data: rawData,
        borderColor: secondary.regular,
        backgroundColor: secondary.regular,
        yAxisID: 'measurement',
        options: {},
      },
      {
        label: 'Cognitive Assistance',
        data: cogData,
        borderColor: primary.regular,
        backgroundColor: primary.regular,
        yAxisID: 'assist',
      },
      {
        label: 'Physical Assistance',
        data: physData,
        borderColor: secondary.light,
        backgroundColor: secondary.light,
        yAxisID: 'assist',
      },
      {
        label: 'Resistance',
        data: resistanceData,
        borderColor: accentPrimary.regular,
        backgroundColor: accentPrimary.regular,
        yAxisID: 'assist',
      },
    ],
  };
  return (
    <Container>
      <Line
        data={_data}
        options={{
          responsive: true,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: { position: 'bottom' },
            title: {
              display: true,
              text: data.header.title.toUpperCase(),
              color: grayscale[0],
              font: {
                family: type.face,
                size: 24,
                weight: '500',
              },
              padding: { bottom: 20 },
            },
            subtitle: {
              display: true,
              text: `${moment(data.header.from_date).format('L')} - ${moment(data.header.to_date).format('L')}`,
            },
            tooltip: {
              callbacks: {
                label: (value) => {
                  let displayValue = value.formattedValue;

                  if (value.dataset.yAxisID === 'measurement' && isStandingDuration) {
                    displayValue = numberToMinutes(Number(value.raw));
                  }

                  if (value.dataset.yAxisID !== 'measurement') {
                    displayValue += '%';
                  }

                  return `${value.dataset.label}: ${displayValue}`;
                },
              },
            },
          },
          scales: {
            measurement: {
              display: true,
              position: 'left',
              ticks: {
                callback: (value) => {
                  return isStandingDuration ? numberToMinutes(Number(value)) : value;
                },
              },
            },
            assist: {
              type: 'linear',
              display: true,
              position: 'right',
              grid: {
                drawOnChartArea: false,
              },
              min: 0,
              max: 100,
              ticks: {
                callback: (value) => {
                  return `${value}%`;
                },
              },
            },
          },
        }}
      ></Line>
    </Container>
  );
};

export default MeasurementChart;
